import { useAuthStore } from '@auth/authStore';
import { addStatus } from '../status/statusActions';
import {
  deleteOrgUserInvite,
  getOrganization,
  getOrganizationMembers,
  getOrgUserInvites,
  listenToOrganizationChanges,
  updateOrganization,
  updateOrganizationMembers,
} from './organizationService';
import { useOrganizationStore } from './organizationStore';
import { useOrgUserInviteStore } from './orgUserInviteStore';
import { useOrgUserStore } from './orgUserStore';
import { Organization, OrganizationUser } from './types';

export const getCurrentOrg = () => {
  const org = useOrganizationStore.getState().organization;
  if (!org) {
    addStatus({
      type: 'error',
      message: 'No organization available',
      autoDismiss: true,
    });
    throw new Error('No organization available');
  }
  return org;
};

export const fetchOrganization = async (organizationId: string) => {
  const store = useOrganizationStore.getState();
  try {
    const organization = await getOrganization(organizationId);

    if (!organization) {
      return;
    }
    store.setOrganization(organization);
  } catch (error) {
    console.error('Error fetching organization:', error);
  }
};

export const updateOrganizationAction = async (
  organization: Partial<Organization> & { id: string },
) => {
  const store = useOrganizationStore.getState();
  try {
    await updateOrganization(organization);
    store.updateOrganization(organization);
    addStatus({
      type: 'success',
      message: 'Organization updated successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error updating organization:', error);
    addStatus({
      type: 'error',
      message: 'Error updating organization',
      autoDismiss: true,
    });
  }
};

export const getOrgMembersAction = async () => {
  const store = useOrgUserStore.getState();
  const orgId = useOrganizationStore.getState().organization?.id;
  if (!orgId) {
    return;
  }
  if (store.loading) {
    return;
  }
  try {
    store.setLoading(true);
    const users = await getOrganizationMembers(orgId);
    if (!users) {
      return;
    }
    store.setItems(users);
  } catch (error) {
    store.setError('Error fetching organization users');
    console.error('Error fetching organization users:', error);
  } finally {
    store.setLoading(false);
  }
};

export const updateOrgMembersActions = async (
  operations: {
    type: 'set' | 'update' | 'delete';
    uid: string;
    data?: Partial<OrganizationUser>;
  }[],
) => {
  const auth = useAuthStore.getState().auth;

  if (operations.find((op) => op.uid === auth?.uid && (op.type !== 'update' || op.data?.role))) {
    console.error('Owners cannot modify their own role or delete their own membership');
    addStatus({
      type: 'error',
      message: 'Owners cannot modify their own role',
      autoDismiss: true,
    });
    return;
  }

  const org = useOrganizationStore.getState().organization;
  try {
    const ops = operations.map((op) => ({
      type: op.type,
      collectionPath: 'memberships',
      id: `${op.uid}_${org?.id}`,
      data: op.data ? { ...op.data, organizationId: org?.id } : null,
    }));

    updateOrganizationMembers(ops);
  } catch (error) {
    console.error('Error updating organization members:', error);
  }
};

export const getOrgUserInvitesAction = async () => {
  const store = useOrgUserInviteStore.getState();
  const orgId = useOrganizationStore.getState().organization?.id;
  if (!orgId) {
    return;
  }
  if (store.loading) {
    return;
  }
  try {
    store.setLoading(true);
    const invites = await getOrgUserInvites(orgId); // Assuming this function fetches invites
    if (!invites) {
      return;
    }
    store.setItems(invites);
  } catch (error) {
    store.setError('Error fetching organization invites');
    console.error('Error fetching organization invites:', error);
  } finally {
    store.setLoading(false);
  }
};

export const deleteOrgUserInviteAction = async (inviteId: string) => {
  const store = useOrgUserInviteStore.getState();
  try {
    await store.deleteItem(inviteId);
    await deleteOrgUserInvite(inviteId);
    addStatus({
      type: 'success',
      message: 'Invite deleted successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error deleting invite:', error);
    addStatus({
      type: 'error',
      message: 'Error deleting invite',
      autoDismiss: true,
    });
    throw error;
  }
};

export const listenToOrgChangesAction = (orgId: string) => {
  const store = useOrganizationStore.getState();
  listenToOrganizationChanges(orgId, (org) => {
    if (!org) {
      console.error('Organization not found');
      return;
    }
    store.setOrganization(org);
  });
};
